import { z } from 'zod';
import { SAFE_USER_ID_REGEX } from '../constants';
import { AGENT_PARAMETER_NAME_REGEX } from './setup/agents';
import type { FlagSource } from './chat';

// ---------------------------------------------------------------------------
// Common param
// ---------------------------------------------------------------------------

export const conversationIdParam = z.object({
  id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Claim
// ---------------------------------------------------------------------------

export const claimBody = z.object({
  hub_id: z.string().uuid(),
  new_team_id: z.string().uuid(),
  new_status: z.string().default('team'),
  new_hub_team_user_id: z.string().optional(),
  new_responder_type: z.string().default('team'),
  previous_status: z.string().optional(),
  previous_responder_type: z.string().optional(),
  previous_agent_id: z.string().uuid().optional(),
  previous_team_id: z.string().uuid().optional(),
  previous_hub_team_user_id: z.string().optional(),
  channel_id: z.string().uuid().optional(),
  hub_type: z.enum(['chat', 'task']).default('chat'),
  // SAFE_USER_ID_REGEX bounds alphabet+length (1–64 of [A-Za-z0-9_-]) so
  // SQL/path/scope-key injection vectors can't reach downstream consumers.
  // Matches `deleteUserConversationsBody.user_id` below.
  user_id: z.string().regex(SAFE_USER_ID_REGEX).optional(),
});

// ---------------------------------------------------------------------------
// Release
// ---------------------------------------------------------------------------

export const releaseBody = z.object({
  hub_id: z.string().uuid(),
  new_status: z.string().optional(),
  new_hub_team_user_id: z.string().optional(),
  new_team_id: z.string().uuid().optional(),
  new_responder_type: z.string().optional(),
  previous_status: z.string().optional(),
  previous_responder_type: z.string().optional(),
  previous_agent_id: z.string().uuid().optional(),
  previous_team_id: z.string().uuid().optional(),
  previous_hub_team_user_id: z.string().optional(),
  channel_id: z.string().uuid().optional(),
  hub_type: z.enum(['chat', 'task']).default('chat'),
  user_id: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Transfer
// ---------------------------------------------------------------------------

export const transferBody = z.object({
  hub_id: z.string().uuid(),
  new_status: z.string().optional(),
  new_responder_type: z.string().optional(),
  new_agent_id: z.string().uuid().optional(),
  new_team_id: z.string().uuid().optional(),
  new_hub_team_user_id: z.string().optional(),
  previous_status: z.string().optional(),
  previous_responder_type: z.string().optional(),
  previous_agent_id: z.string().uuid().optional(),
  previous_team_id: z.string().uuid().optional(),
  previous_hub_team_user_id: z.string().optional(),
  channel_id: z.string().uuid().optional(),
  hub_type: z.enum(['chat', 'task']).default('chat'),
  user_id: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Close
// ---------------------------------------------------------------------------

export const closeBody = z.object({
  hub_id: z.string().uuid(),
  previous_status: z.string().optional(),
  previous_responder_type: z.string().optional(),
  previous_agent_id: z.string().uuid().optional(),
  previous_team_id: z.string().uuid().optional(),
  previous_hub_team_user_id: z.string().optional(),
  channel_id: z.string().uuid().optional(),
  hub_type: z.enum(['chat', 'task']).default('chat'),
  user_id: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Kanban Update
// ---------------------------------------------------------------------------

/**
 * Wire payload for `kanbanUpdateBody.additional_context` and the equivalent
 * MCP `update_kanban_status` tool input. Server-side validates the payload
 * shape against the target kanban status's `additional_context_schema`
 * (a JSON Schema). Bounded to 16 KB JSON-serialized — mirrors the same cap
 * on the schema field itself in `setup/kanban.ts`.
 *
 * Single source of truth for the `additional_context` shape: consumed by
 * `kanbanUpdateBody` below (the REST `PATCH /:id/kanban` contract). Exported
 * so any client constructing the same payload validates against one schema.
 */
export const additionalContextPayloadSchema = z
  .record(z.unknown())
  .refine((r) => JSON.stringify(r).length <= 16384, {
    message: 'additional_context payload must be at most 16384 bytes when JSON-serialized',
  });

export const kanbanUpdateBody = z.object({
  hub_id: z.string().uuid(),
  // 50-char slug cap + headroom; bounds the "unknown kanban status" 400 echo.
  new_kanban_status: z.string().max(64),
  previous_kanban_status: z.string().max(64).optional(),
  channel_id: z.string().uuid().optional(),
  hub_type: z.enum(['chat', 'task']).default('chat'),
  user_id: z.string().optional(),
  scheduled_event_date: z.string().optional(),
  additional_context: additionalContextPayloadSchema.optional(),
  event_description: z.string().optional(),
});

// Post-hoc annotation (business outcome) set on a conversation after it ends —
// e.g. customer_purchased. `key` becomes a `data.annotations.<key>` analytics
// dimension, so it must be a safe SQL identifier. `type` drives both value
// coercion and how the auto-registered dimension aggregates.
export const annotateConversationBody = z.object({
  hub_id: z.string().uuid(),
  key: z
    .string()
    .regex(
      AGENT_PARAMETER_NAME_REGEX,
      'key must be a valid identifier (letter/underscore start; letters, digits, underscore; max 64 chars)',
    ),
  value: z.union([z.string().max(1024), z.number(), z.boolean()]),
  type: z.enum(['numeric', 'categorical', 'text']).default('categorical'),
});

// ---------------------------------------------------------------------------
// Observability
// ---------------------------------------------------------------------------

export const observabilityParams = z.object({
  conversationId: z.string().uuid(),
  messageId: z.string().uuid(),
});

// List variant — same conversationId shape as `observabilityParams`, derived
// via `.pick` so the field stays in lockstep with the single-message route.
export const observabilityListParams = observabilityParams.pick({ conversationId: true });

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type ConversationIdParam = z.infer<typeof conversationIdParam>;
export type ClaimBody = z.infer<typeof claimBody>;
export type ReleaseBody = z.infer<typeof releaseBody>;
export type TransferBody = z.infer<typeof transferBody>;
export type CloseBody = z.infer<typeof closeBody>;
export type KanbanUpdateBody = z.infer<typeof kanbanUpdateBody>;
export type AnnotateConversationBody = z.infer<typeof annotateConversationBody>;
export type ObservabilityParams = z.infer<typeof observabilityParams>;
export type ObservabilityListParams = z.infer<typeof observabilityListParams>;

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

/** Result of a conversation operation (claim, release, transfer, close) */
export interface ConversationOperationResponse {
  success: true;
  operation_id: string;
  operation_type: 'claim' | 'release' | 'transfer' | 'close';
  conversation_id: string;
}

/** Result of a kanban status update operation */
export interface KanbanUpdateResponse {
  success: true;
  operation_id: string;
  operation_type: 'update_kanban_status';
  conversation_id: string;
  kanban_status_updated_at?: string;
  /** Set when the new kanban_status is terminal and the conversation auto-closed. */
  closed?: boolean;
}

/** Result of a post-hoc annotation set operation */
export interface AnnotateConversationResponse {
  success: true;
  operation_type: 'set_annotation';
  conversation_id: string;
  key: string;
}

/** Result of a conversation reopen operation */
export interface ReopenConversationResponse {
  data: Record<string, unknown>;
}

/** Observability data for a message */
export interface ObservabilityResponse {
  data: import('../observability/types').LLMObservabilityRecord;
}

/**
 * List of per-message observability summaries for a conversation.
 * Carries metadata.json contents only (timestamp, latency, agent, tool counts)
 * — drill into a specific message for full provider/model/tokens/messages.
 */
export interface ObservabilityListResponse {
  data: {
    hub_id: string;
    conversation_id: string;
    entries: import('../observability/types').WayAIMetadataRecord[];
    truncated: boolean;
  };
}

// ---------------------------------------------------------------------------
// Delete User Conversations
//
// Hub-admin only. `mode='current'` wipes a single conversation; `mode='all'`
// wipes every conversation for the target user on this hub. Deletion is
// hub-scoped — same `user_id` on a sibling hub is untouched. Permanent.
// ---------------------------------------------------------------------------

export const deleteUserConversationsBody = z.discriminatedUnion('mode', [
  z.object({
    hub_id: z.string().uuid(),
    user_id: z.string().regex(SAFE_USER_ID_REGEX),
    mode: z.literal('all'),
  }),
  z.object({
    hub_id: z.string().uuid(),
    user_id: z.string().regex(SAFE_USER_ID_REGEX),
    mode: z.literal('current'),
    conversation_id: z.string().uuid(),
  }),
]);
export type DeleteUserConversationsBody = z.infer<typeof deleteUserConversationsBody>;

export interface DeleteUserConversationsResponse {
  success: true;
  deleted_count: number;
}

// ---------------------------------------------------------------------------
// Send Message (hub testing — preview or production)
// ---------------------------------------------------------------------------

export const sendMessageBody = z.object({
  hub_id: z.string().uuid(),
  message: z.string().min(1).max(50000),
  /** For chat hubs: optional — automatically continues the user's active conversation (or creates one).
   *  For task hubs: required to continue an existing conversation. Omit to start a new task conversation. */
  conversation_id: z.string().uuid().optional(),
});
export type SendMessageBody = z.infer<typeof sendMessageBody>;

export interface SendMessageResponse {
  conversation_id: string;
  hub_type: 'chat' | 'task';
  user_message_id: string;
  agent_reply: {
    message_id: string;
    message_text: string;
  } | null;
  status: 'replied' | 'skipped' | 'error' | 'empty_response';
  status_reason?: string;
}

// ---------------------------------------------------------------------------
// List Conversations (admin)
// ---------------------------------------------------------------------------

export const listConversationsQuery = z.object({
  hub_id: z.string().uuid(),
  status: z.enum(['agent', 'team', 'ended']).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListConversationsQuery = z.infer<typeof listConversationsQuery>;

export interface GetConversationsResponse {
  conversations: Array<{
    conversation_id: string;
    conversation_status: string;
    current_responder_type: string | null;
    kanban_status: string | null;
    created_at: string;
    updated_at: string | null;
    user_name: string | null;
    user_email: string | null;
    is_flagged: boolean;
    flag_source: FlagSource;
  }>;
  total: number;
}

// ---------------------------------------------------------------------------
// Get Conversation Detail (with messages)
// ---------------------------------------------------------------------------

export const getConversationDetailParams = z.object({
  id: z.string().uuid(),
});
export type GetConversationDetailParams = z.infer<typeof getConversationDetailParams>;

export const getConversationDetailQuery = z.object({
  hub_id: z.string().uuid(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type GetConversationDetailQuery = z.infer<typeof getConversationDetailQuery>;

export interface GetConversationDetailResponse {
  conversation: {
    conversation_id: string;
    conversation_status: string;
    current_responder_type: string | null;
    access_status: string;
    kanban_status: string | null;
    created_at: string;
    updated_at: string | null;
    user_name: string | null;
    user_email: string | null;
    is_flagged: boolean;
    flag_source: FlagSource;
  };
  messages: Array<{
    message_id: string;
    message_text: string | null;
    sender_type: string;
    receiver_type: string | null;
    created_at: string;
    message_type?: string;
    channel_type?: string | null;
    keep_in_history?: boolean;
    agent_id?: string | null;
    sender_id?: string | null;
    receiver_id?: string | null;
    sender_description?: string;
    receiver_description?: string;
    metadata?: Record<string, unknown> | null;
    message_status?: string | null;
  }>;
  total_messages: number;
}

// ---------------------------------------------------------------------------
// WhatsApp template picker (closed-window team re-engagement)
//
// When a WhatsApp conversation is past Meta's 24h messaging window, a free-form
// team reply is blocked. An approved WhatsApp template is the only compliant
// re-engagement path. These contracts back the picker: list the conversation's
// approved templates, then send one with body variables filled in.
// ---------------------------------------------------------------------------

export const listWhatsAppTemplatesQuery = z.object({
  hub_id: z.string().uuid(),
});
export type ListWhatsAppTemplatesQuery = z.infer<typeof listWhatsAppTemplatesQuery>;

export const sendWhatsAppTemplateBody = z.object({
  hub_id: z.string().uuid(),
  /** HubDO outbound_template.template_id. */
  template_id: z.string().min(1).max(128),
  /** The conversation's end user (customer). Bounded like `claimBody.user_id`. */
  user_receiver_id: z.string().regex(SAFE_USER_ID_REGEX),
  /** Body-variable values: positional array or key→value record. Bounded so an
   *  authenticated caller can't post an oversized payload to the transcript. */
  variables: z
    .union([
      z.array(z.string().max(2048)).max(50),
      z
        .record(z.string().max(128), z.string().max(2048))
        .refine((r) => Object.keys(r).length <= 50, 'too many variables'),
    ])
    .optional(),
});
export type SendWhatsAppTemplateBody = z.infer<typeof sendWhatsAppTemplateBody>;

/** One approved WhatsApp template, shaped for the picker UI. */
export interface WhatsAppTemplatePickerItem {
  template_id: string;
  display_name: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  language: string;
  /** BODY component text, with `{{N}}` / `{{name}}` placeholders intact. */
  body_text: string;
  parameter_format: 'POSITIONAL' | 'NAMED';
  /** Distinct variable keys in render order — drives the fill-in form. */
  variables: string[];
}

export interface ListWhatsAppTemplatesResponse {
  data: WhatsAppTemplatePickerItem[];
}

/**
 * Send result on success. Failures return a non-2xx with the standard error
 * envelope (`{ error, message }`) whose `error` carries a stable code the
 * picker maps to a message — e.g. `template_not_approved`, `conversation_ended`,
 * `missing_variables`, `meta_rejected:<n>`.
 */
export interface SendWhatsAppTemplateResponse {
  data: { message_id: string; channel_message_sid: string | null; rendered_text: string };
}
